import QueryString from 'query-string'
import { useCallback, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { isString, isNotEmpty } from '../../utils/string'

export type Value = string | number | boolean | null | undefined | object
export type SearchState = Record<string, Array<Value> | Value>
export type DispatchSearchState<S> = (value: S | ((prevState: S) => S)) => void
export type SearchStateResult<S> = [S, DispatchSearchState<S>]

/**
 * Determines if a Value is non nullable, an empty string or the string 'null'.
 */
function isValidValue(value: Value): boolean {
  return value != null && (!isString(value) || (isNotEmpty(value) && value.trim() !== 'null'))
}

/**
 * Remove values that are `null` or empty strings. If a value is an array,
 * filter out `null` and empty strings from it. If after filtering the array it
 * becomes empty, it is also removed.
 */
function filterValues(state: SearchState): SearchState {
  const result: SearchState = {}

  for (const [key, value] of Object.entries(state)) {
    if (Array.isArray(value)) {
      const filteredArray = value.filter(isValidValue)
      result[key] = filteredArray.length > 0 ? filteredArray : undefined
    } else {
      result[key] = isValidValue(value) ? value : undefined
    }
  }

  return result
}

/**
 * For every Array value, sort it's elements.
 */
function sortArrayValues(state: SearchState): SearchState {
  for (const value of Object.values(state)) {
    if (Array.isArray(value)) {
      value.sort()
    }
  }

  return state
}

/**
 * Given a search string, returns a parsed `State` from it.
 */
function parseSearch(search: string, parseNumbers = true): SearchState {
  const parsedState = QueryString.parse(search, {
    arrayFormat: 'bracket-separator',
    arrayFormatSeparator: ',',
    parseBooleans: true,
    parseNumbers,
  })

  return sortArrayValues(filterValues(parsedState))
}

/**
 * Given a `State`, returns a `string` to be used as search string.
 */
function stringifyState(state: SearchState): string {
  const filteredState = filterValues(state)

  return QueryString.stringify(filteredState, {
    sort: false,
    arrayFormat: 'bracket-separator',
    arrayFormatSeparator: ',',
  })
}

/**
 * React custom hook that acts like the native `useState`, however, instead of
 * setting the state into memory, it sets the state into the search string of
 * the URL and react to changes on it.
 *
 * Because of the limitations of representing nested objects on the search
 * string, the `State` must be an `Object` with just one depth level, where the
 * keys must be an URL valid `string` and the possible values types are
 * `string`, `number`, `boolean`, `null` and `undefined`, or an `Array` of those
 * values mixed. The `number` and `boolean` types are automatically parsed, but
 * omitting those values do not result that they will be parsed to `0` or
 * `false` respectively.
 * Values like `null`, `undefined` and empty `strings` are omitted from the URL
 * and `Array` containing only these values are also omitted. As result of this,
 * the behavior is destructive, i.e., empty `strings` and `null` are accepted,
 * but they are not persisted across operations, so you should not rely on
 * them.
 *
 * NOTE: This hook is composed by the `react-router-dom` hooks `useHistory` and
 * `useLocation`, so it must be rendered inside a Router.
 */
export function useSearchState<S extends SearchState>(parseNumbers = true): SearchStateResult<S> {
  const { replace } = useHistory()
  const { pathname, search, hash } = useLocation()

  const state = useMemo(() => parseSearch(search, parseNumbers) as S, [parseNumbers, search])

  const dispatcher = useCallback(
    (value: S | ((prevState: S) => S)): void => {
      let newState

      if (typeof value === 'function') {
        newState = value(state)
      } else {
        newState = value
      }

      const newSearch = stringifyState(newState)

      replace(`${pathname}?${newSearch}${hash}`)
    },
    [replace, pathname, hash, state]
  )

  return [state, dispatcher]
}
